'use client';

import {
  BreadcrumbsCurrentItem,
  BreadcrumbsItem,
  BreadcrumbsLink,
  BreadcrumbsList,
  BreadcrumbsRoot,
  Breakpoint,
  Divider,
  Heading,
  Stack,
  StackItem,
  Text,
} from '@pie/amber';
import NextLink from 'next/link';
import { Fragment, ReactNode } from 'react';
import { usePageContext } from './PageContext';
import { PageHeroBackground } from './PageHeroBackground';

type Breadcrumb =
  | {
      current?: never;
      href: string;
      label: string;
      loading?: never;
    }
  | {
      current: true;
      href?: never;
      label: string;
      loading?: never;
    }
  | {
      current?: never;
      href?: never;
      label: string;
      loading: true;
    };

interface Illustration {
  label: string;
  src: string;
}

export interface PageHeaderProps {
  actionButton?: ReactNode;
  breadcrumbs?: Breadcrumb[];
  children?: ReactNode;
  description?: ReactNode;
  illustration?: Illustration;
  title: ReactNode;
}

const heroBleedStyle = {
  md: 'pb-[136px]',
  none: '',
  sm: 'pb-[77px]',
};

export const PageHeader = ({
  actionButton,
  breadcrumbs = [],
  children,
  description,
  illustration,
  title,
}: PageHeaderProps) => {
  const { accent, heroBleedSize } = usePageContext();

  return (
    <div
      className={`
        ${heroBleedStyle[heroBleedSize]}
        ${accent ? 'relative isolate' : ''}
        pt-layout-lg
        px-layout-xs md:px-layout-lg
        rounded-t-layout-lg
      `}
    >
      {accent && <PageHeroBackground />}
      <div
        className="
          relative
          mx-auto
          max-w-[744px]
        "
      >
        <div className="mb-layout-xl">
          {breadcrumbs.length > 0 && (
            <BreadcrumbsRoot onContrast={accent}>
              <BreadcrumbsList>
                {breadcrumbs.map((breadcrumb) => (
                  <Fragment key={breadcrumb.label}>
                    {breadcrumb.current ? (
                      <BreadcrumbsCurrentItem>{breadcrumb.label}</BreadcrumbsCurrentItem>
                    ) : (
                      <BreadcrumbsItem>
                        {breadcrumb.loading ? (
                          '...'
                        ) : (
                          <BreadcrumbsLink as={NextLink} href={breadcrumb.href}>
                            {breadcrumb.label}
                          </BreadcrumbsLink>
                        )}
                      </BreadcrumbsItem>
                    )}
                  </Fragment>
                ))}
              </BreadcrumbsList>
            </BreadcrumbsRoot>
          )}
          <Stack direction="row" gap="layout-xl">
            <StackItem>
              <Heading
                accent
                className="pt-xs"
                onContrast={accent ? 'default' : undefined}
                size={{ base: 'xl', md: '2xl' }}
              >
                {title}
              </Heading>
              {description && (
                <Text
                  as="div"
                  className="mt-layout-xs"
                  onContrast={accent ? 'default' : undefined}
                  variant="body-sm"
                >
                  {description}
                </Text>
              )}
            </StackItem>
            {illustration && (
              <Breakpoint above="sm">
                <StackItem shrink={false}>
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img alt={illustration.label} height="128" src={illustration.src} width="128" />
                </StackItem>
              </Breakpoint>
            )}
          </Stack>
          {actionButton && <div className="mt-layout-md">{actionButton}</div>}
          {children && <Divider className="mt-layout-md" onContrast={accent} />}
        </div>
        {children}
      </div>
    </div>
  );
};
